/**
 * Subgroup collapsing (brief 5.2 step 6, QC-11).
 *
 * Sparse levels are combined into one labeled level; every other
 * level stays its own group. The proposal is an explicit rule
 * object with before/after counts — it takes effect only when
 * applied through applyCollapses, which returns a new table whose
 * subgroup column holds group keys, plus a transformation log.
 * Groups are ordered by level value (the order reports use), with
 * the combined group last.
 */
import { columnIndex } from '@sae/io';
import { compareValues, labelOf, valueCountsOf } from './profile.js';
/** Default label for the combined sparse level (brief QC-11). */
export const DEFAULT_COMBINED_LABEL = 'Combined (small groups)';
/**
 * Propose a collapsing rule for one subgroup variable. Levels with
 * statewide counts at or above the threshold stay singleton groups;
 * the rest combine. Returns null when no level is sparse (nothing
 * to propose).
 */
export function proposeCollapse(table, variable, options = {}) {
    const sparseBelow = options.sparseBelow ?? 30;
    const combinedLabel = options.combinedLabel ?? DEFAULT_COMBINED_LABEL;
    const beforeCounts = valueCountsOf(table, variable);
    const sparse = beforeCounts.filter((vc) => vc.count < sparseBelow);
    if (sparse.length === 0)
        return null;
    const groups = [];
    const singletons = beforeCounts
        .filter((vc) => vc.count >= sparseBelow)
        .sort((a, b) => compareValues(a.value, b.value));
    for (const vc of singletons) {
        groups.push({
            key: vc.value,
            label: vc.label ?? `Code ${String(vc.value)}`,
            sourceValues: [vc.value],
            respondentCount: vc.count,
        });
    }
    const combinedCount = sparse.reduce((a, vc) => a + vc.count, 0);
    groups.push({
        key: combinedLabel,
        label: combinedLabel,
        sourceValues: sparse.map((vc) => vc.value),
        respondentCount: combinedCount,
    });
    return {
        variable,
        groups,
        combinedValues: sparse.map((vc) => vc.value),
        combinedLabel,
        beforeCounts,
    };
}
/** The group key a raw subgroup value maps to under a rule. */
export function collapseValue(value, rule) {
    if (value === null)
        return null;
    for (const group of rule.groups) {
        if (group.sourceValues.some((v) => v === value))
            return group.key;
    }
    return null; // value the rule does not cover: joins no level (logged on apply)
}
/**
 * Apply collapsing rules to a table (new table out; input intact).
 * Values a rule does not cover become missing for that variable
 * and are counted in the log.
 */
export function applyCollapses(table, rules) {
    const log = [];
    const byIndex = new Map();
    for (const rule of rules) {
        const idx = columnIndex(table, rule.variable);
        if (idx >= 0)
            byIndex.set(idx, rule);
    }
    const afterCounts = new Map();
    const uncovered = new Map();
    for (const rule of rules) {
        afterCounts.set(rule.variable, new Map());
        uncovered.set(rule.variable, 0);
    }
    const rows = table.rows.map((row) => row.map((cell, idx) => {
        const rule = byIndex.get(idx);
        if (rule === undefined || cell === null)
            return cell;
        const key = collapseValue(cell, rule);
        const counts = afterCounts.get(rule.variable);
        if (key === null) {
            uncovered.set(rule.variable, (uncovered.get(rule.variable) ?? 0) + 1);
            return null;
        }
        counts.set(key, (counts.get(key) ?? 0) + 1);
        return key;
    }));
    for (const rule of rules) {
        const counts = {};
        const ac = afterCounts.get(rule.variable);
        if (ac !== undefined) {
            for (const [k, v] of ac)
                counts[String(k)] = v;
        }
        const unc = uncovered.get(rule.variable) ?? 0;
        if (unc > 0)
            counts['uncovered'] = unc;
        const labelText = (v) => labelOf(table, rule.variable, v) ?? `Code ${String(v)}`;
        log.push({
            kind: 'subgroup-collapse',
            column: rule.variable,
            ruleId: `${rule.variable}:collapse`,
            counts,
            note: `Collapsed "${rule.variable}": ${rule.combinedValues
                .map((v) => `${labelText(v)} (${String(v)})`)
                .join(', ')} combined into "${rule.combinedLabel}".` +
                (unc > 0
                    ? ` ${unc} values were not covered by the rule and were treated as missing.`
                    : ''),
        });
    }
    return { table: { ...table, rows }, log };
}
/** Engine subgroup levels for a collapsed variable, in group order. */
export function subgroupLevelsOf(rule) {
    return rule.groups.map((g) => ({ value: g.key, label: g.label }));
}
//# sourceMappingURL=collapse.js.map